import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Modal } from '@carbon/react';
import { Download, Maximize, Minimize, Table } from '@carbon/icons-react';
import { createLightweightSensorChart } from './lightweightChartEngine';

/**
 * SensorChart — Lightweight Charts time-series for sensor data (matches native app styling).
 *
 * Preserves existing web UX:
 *   - Crosshair tooltip with local-time formatting
 *   - "View data table" modal with full date/time
 *   - CSV export (uses the caller's onExport handler — keeps full-resolution data)
 *   - Fullscreen toggle
 */
export default function SensorChart({
  data,
  label,
  unit,
  color,
  timeRange,
  isDark = false,
  onExport,
  loading = false,
  emptyLabel = 'No data available for selected time range',
  thresholdMin = null,
  thresholdMax = null,
  thresholdTarget = null,
  showThresholdLines = true,
  statsMin = null,
  statsMax = null,
  statsAvg = null,
}) {
  const containerRef = useRef(null);
  const chartApiRef = useRef(null);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const textColor = isDark ? '#c6c6c6' : '#525252';
  const gridColor = isDark ? '#393939' : '#e0e0e0';
  const chartHeight = isFullscreen ? Math.max(window.innerHeight - 80, 280) : 400;

  const chartOptions = useMemo(
    () => ({
      data,
      label,
      unit,
      color,
      timeRange,
      isDark,
      thresholdMin,
      thresholdMax,
      thresholdTarget,
      showThresholdLines,
      statsMin,
      statsMax,
      statsAvg,
    }),
    [
      data,
      label,
      unit,
      color,
      timeRange,
      isDark,
      thresholdMin,
      thresholdMax,
      thresholdTarget,
      showThresholdLines,
      statsMin,
      statsMax,
      statsAvg,
    ]
  );

  const handleTableExport = useCallback(() => {
    if (onExport) onExport(label);
  }, [onExport, label]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((f) => !f);
  }, []);

  useEffect(() => {
    if (!containerRef.current || loading || !data?.length) {
      chartApiRef.current?.destroy();
      chartApiRef.current = null;
      return undefined;
    }

    if (!chartApiRef.current) {
      chartApiRef.current = createLightweightSensorChart(containerRef.current);
    }

    const width = containerRef.current.clientWidth || 300;
    chartApiRef.current.update({ ...chartOptions, width, height: chartHeight });

    return undefined;
  }, [chartOptions, chartHeight, loading, data]);

  useEffect(() => {
    if (!containerRef.current || !chartApiRef.current) return undefined;

    const resize = () => {
      const width = containerRef.current?.clientWidth;
      if (width) {
        chartApiRef.current?.resize(width, chartHeight);
        chartApiRef.current?.update({ ...chartOptions, width, height: chartHeight });
      }
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(containerRef.current);
    window.addEventListener('resize', resize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [chartOptions, chartHeight, isFullscreen]);

  useEffect(
    () => () => {
      chartApiRef.current?.destroy();
      chartApiRef.current = null;
    },
    []
  );

  if (loading) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textColor }}>
        Loading chart data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: textColor }}>
        {emptyLabel}
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          ...(isFullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: isDark ? '#161616' : '#ffffff',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
              }
            : {}),
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, marginBottom: 4, flexShrink: 0 }}>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Table}
            iconDescription="View data table"
            hasIconOnly
            tooltipPosition="left"
            onClick={() => setTableModalOpen(true)}
          />
          <Button
            kind="ghost"
            size="sm"
            renderIcon={isFullscreen ? Minimize : Maximize}
            iconDescription={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            hasIconOnly
            tooltipPosition="left"
            onClick={toggleFullscreen}
          />
        </div>
        <div
          ref={containerRef}
          style={{ height: chartHeight, width: '100%', flex: 1 }}
        />
      </div>

      <Modal
        open={tableModalOpen}
        modalHeading={`${label} Data`}
        passiveModal
        onRequestClose={() => setTableModalOpen(false)}
        size="lg"
      >
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <Button kind="tertiary" size="sm" renderIcon={Download} onClick={handleTableExport}>
            Export CSV
          </Button>
        </div>
        <div style={{ maxHeight: '50vh', overflow: 'auto', border: `1px solid ${gridColor}`, borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: isDark ? '#262626' : '#f4f4f4', zIndex: 1 }}>
              <tr>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${gridColor}`, color: textColor }}>
                  Date &amp; Time
                </th>
                <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: `1px solid ${gridColor}`, color: textColor }}>
                  {label} ({unit})
                </th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((d, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${gridColor}` }}>
                  <td style={{ padding: '6px 12px', color: textColor }}>{d.dateTime || new Date(d.date).toLocaleString()}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', color: textColor }}>
                    {d.value != null ? Number(d.value).toFixed(2) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}
